import React, { memo, useMemo, useState, useCallback, useRef } from 'react';
import { Badge, Icon, Menus, Text, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import { CAMPAIGNS_MARKETING } from 'constants/Entities';

import { getDateFromBackendISO } from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';
import { useSelector } from 'react-redux';
import { canManageMarketingCampaigns } from '../../../utils/marketingCampaignPermissions';

import {
    getMarketingCampaignStatusBadgeType,
    getMarketingCampaignStatusLabel,
    MARKETING_CAMPAIGN_STATUS,
    normalizeMarketingCampaignStatusId,
} from '../../../utils/marketingCampaignStatus';

import { useCloseOnAncestorScroll } from 'utils/hooks/useCloseOnAncestorScroll';

import './styles.scss';

function formatCount(value, isDraft) {
    if (isDraft) return '-';

    const n = Number(value);
    const t = Number.isFinite(n) ? Math.trunc(n) : 0;

    return t.toLocaleString();
}

function sharePct(part, total) {
    const p = Number(part);
    const t = Number(total);
    if (!Number.isFinite(t) || t <= 0) return null;
    const pn = Number.isFinite(p) ? Math.trunc(p) : 0;
    return `${((pn / t) * 100).toFixed(2)}%`;
}

/** Prefer launch → schedule → updated → created for sent campaigns. */
function pickSentOnDateBlock({ launchAt, scheduleAt, updatedAt, createdAt }) {
    if (launchAt?.time) return launchAt;
    if (scheduleAt?.time) return scheduleAt;
    if (updatedAt?.time) return updatedAt;
    return createdAt;
}

/**
 * Row subtitle: `{status} — {literal with date params}`.
 * Non-sent (Draft, Scheduled, Sending, Cancelled, Error): `label_last_modified` (updated → created).
 * Sent: `label_date_with_time` (launch → schedule → updated → created).
 */
function campaignRowSubtitle({
    statusId,
    statusLabel,
    launchAt,
    scheduleAt,
    updatedAt,
    createdAt,
}) {
    const isSent = statusId === MARKETING_CAMPAIGN_STATUS.SENT;

    let time;
    if (isSent) {
        const dateBlock = pickSentOnDateBlock({ launchAt, scheduleAt, updatedAt, createdAt });
        time = dateBlock?.time;
    } else {
        time = updatedAt?.time ?? createdAt?.time;
    }

    if (!time) {
        return null;
    }

    if (isSent) {
        const datePart = getDateFromBackendISO(time, 'LL');
        const timePart = getDateFromBackendISO(time, 'LT');
        const detail = getLiteralWithParameters('label_date_with_time', [datePart, timePart]);
        return `${statusLabel} - ${detail}`;
    }

    const datePart = getDateFromBackendISO(time, 'LL');
    const timePart = getDateFromBackendISO(time, 'HH:mm');
    const detail = getLiteralWithParameters('label_last_modified', [datePart, timePart]);
    return `${statusLabel} - ${detail}`;
}

function statusPresentation(status) {
    const id = normalizeMarketingCampaignStatusId(status);
    return {
        badgeType: getMarketingCampaignStatusBadgeType(id),
        label: getMarketingCampaignStatusLabel(id),
    };
}

function MarketingCampaignRow({
    data = {},
    onPreviewAndTest,
    onDuplicateCampaign,
    onDeleteCampaign,
    onOpenDetail,
    colDef,
}) {
    const theme = useTheme();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuInstanceKey, setMenuInstanceKey] = useState(0);
    const actionsRef = useRef(null);

    const handleMenuVisibleChange = useCallback((visible) => {
        setIsMenuOpen(visible);
    }, []);

    const closeRowMenu = useCallback(() => {
        setIsMenuOpen(false);
        setMenuInstanceKey((key) => key + 1);
    }, []);

    const closeMenuFromScroll = useCallback(() => {
        setIsMenuOpen(false);
        setMenuInstanceKey((key) => key + 1);
    }, []);

    useCloseOnAncestorScroll({
        enabled: isMenuOpen,
        anchorRef: actionsRef,
        onClose: closeMenuFromScroll,
    });
    const {
        id: campaignId,
        name,
        status,
        totalDelivered,
        totalReaders,
        totalClickers,
        totalCanceled,
        launchAt,
        scheduleAt,
        createdAt,
        updatedAt,
    } = data;
    const canManage = useSelector(canManageMarketingCampaigns);
    const statusId = normalizeMarketingCampaignStatusId(status);
    const { badgeType, label: statusLabel } = statusPresentation(status);
    const subtitle = campaignRowSubtitle({
        statusId,
        statusLabel,
        launchAt,
        scheduleAt,
        updatedAt,
        createdAt,
    });

    const isDraft = statusId === MARKETING_CAMPAIGN_STATUS.DRAFT;
    const isSent = statusId === MARKETING_CAMPAIGN_STATUS.SENT;
    const canClickRow = isSent || canManage;

    const metrics = [
        {
            labelKey: 'label_marketing_campaigns_metrics_delivered',
            value: formatCount(totalDelivered, isDraft),
            rate: null,
            wide: false,
        },
        {
            labelKey: 'label_marketing_campaigns_metrics_opens',
            value: formatCount(totalReaders, isDraft),
            rate: sharePct(totalReaders, totalDelivered),
            wide: false,
        },
        {
            labelKey: 'label_marketing_campaigns_metrics_clicks',
            value: formatCount(totalClickers, isDraft),
            rate: sharePct(totalClickers, totalDelivered),
            wide: false,
        },
        {
            labelKey: 'label_marketing_campaigns_metrics_unsubscribed',
            value: formatCount(totalCanceled, isDraft),
            rate: sharePct(totalCanceled, totalDelivered),
            wide: true,
        },
    ];

    const openPreviewAndTest = onPreviewAndTest ?? colDef?.cellRendererParams?.onPreviewAndTest;
    const openDuplicate = onDuplicateCampaign ?? colDef?.cellRendererParams?.onDuplicateCampaign;
    const openDelete = onDeleteCampaign ?? colDef?.cellRendererParams?.onDeleteCampaign;
    const openDetails = onOpenDetail ?? colDef?.cellRendererParams?.onOpenDetail;

    const handleRowSurfaceClick = useCallback(
        (event) => {
            if (actionsRef.current?.contains(event.target)) {
                return;
            }
            openDetails?.(data);
        },
        [data, openDetails],
    );

    const menuItems = useMemo(() => {
        if (!campaignId) return [];

        const items = [
            {
                title: <Text>{getLiteral('label_marketing_campaigns_preview_and_test')}</Text>,
                icon: <Icon name="visibilityOn" />,
                onClick: () => {
                    closeRowMenu();
                    openPreviewAndTest?.(campaignId);
                },
            },
            ...(canManage
                ? [
                      {
                          title: <Text>{getLiteral('action_duplicate')}</Text>,
                          icon: <Icon name="duplicate" />,
                          onClick: () => {
                              closeRowMenu();
                              openDuplicate?.(campaignId);
                          },
                      },
                      { divider: true },
                      {
                          title: (
                              <Text color={theme.colors.textLight.negative}>
                                  {getLiteral('action_delete')}
                              </Text>
                          ),
                          icon: <Icon name="delete" color={theme.colors.semantic.negative500} />,
                          onClick: () => {
                              closeRowMenu();
                              openDelete?.(campaignId);
                          },
                      },
                  ]
                : []),
        ];

        return items;
    }, [
        campaignId,
        theme.colors.textLight.negative,
        theme.colors.semantic.negative500,
        closeRowMenu,
        openDelete,
        openPreviewAndTest,
        openDuplicate,
        canManage,
    ]);

    const editPath = `${CAMPAIGNS_MARKETING.route}/edit/${campaignId}`;

    const actionKeyDown = (handler) => (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handler();
        }
    };

    // Loading row: render empty placeholder to avoid misaligned elements
    if (!campaignId) {
        return <div className="fm-marketing-campaign-row fm-marketing-campaign-row--loading" />;
    }

    return (
        <div
            className={classnames('fm-marketing-campaign-row', {
                'fm-marketing-campaign-row--clickable': canClickRow,
                'fm-marketing-campaign-row--menu-open': isMenuOpen,
            })}
            onClick={canClickRow ? handleRowSurfaceClick : undefined}
        >
            <div className="fm-marketing-campaign-row__info">
                <div className="fm-marketing-campaign-row__title-wrap">
                    <Text
                        className="fm-marketing-campaign-row__title"
                        type="subtitle"
                        medium
                        isTruncated
                        useTooltip
                    >
                        {name ?? ''}
                    </Text>
                </div>
                {subtitle && (
                    <Text
                        className="fm-marketing-campaign-row__subtitle"
                        color={theme.colors.utility.textSecondary}
                        type="caption"
                        isTruncated
                        useTooltip
                    >
                        {subtitle}
                    </Text>
                )}
            </div>
            <div className="fm-marketing-campaign-row__status-column">
                <Badge
                    className="fm-marketing-campaign-row__status"
                    isTruncated
                    useTooltip
                    type={badgeType}
                >
                    {statusLabel}
                </Badge>
            </div>
            <div className="fm-marketing-campaign-row__metrics">
                {metrics.map(({ labelKey, value, rate, wide }) => (
                    <div
                        key={labelKey}
                        className={classnames('fm-marketing-campaign-row__metric', {
                            'fm-marketing-campaign-row__metric--wide': wide,
                        })}
                    >
                        <Text
                            className="fm-marketing-campaign-row__metric-label"
                            color={theme.colors.utility.textSecondary}
                            type="caption"
                        >
                            {getLiteral(labelKey)}
                        </Text>
                        <Text
                            className="fm-marketing-campaign-row__metric-value"
                            type="subtitle"
                            medium
                        >
                            {value}
                        </Text>
                        {rate != null ? (
                            <Text
                                className="fm-marketing-campaign-row__metric-rate"
                                color={theme.colors.utility.textSecondary}
                                type="badges"
                            >
                                {rate}
                            </Text>
                        ) : null}
                    </div>
                ))}
            </div>
            {campaignId ? (
                <div className="fm-marketing-campaign-row__actions" ref={actionsRef}>
                    <div className="fm-marketing-campaign-row__actions-toolbar">
                        <div className="fm-marketing-campaign-row__primary-action">
                            {isSent ? (
                                <Tooltip
                                    content={getLiteral('action_view_detail_single')}
                                    placement="top"
                                >
                                    <span
                                        className="fm-marketing-campaign-row__action-btn"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => openDetails?.(data)}
                                        onKeyDown={actionKeyDown(() => openDetails?.(data))}
                                    >
                                        <Icon
                                            name="file"
                                            color={theme.colors.actionMinor[500]}
                                            size="large"
                                        />
                                    </span>
                                </Tooltip>
                            ) : canManage ? (
                                <Tooltip content={getLiteral('action_edit')} placement="top">
                                    <span
                                        className="fm-marketing-campaign-row__action-btn"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => ensureRoute(editPath)}
                                        onKeyDown={actionKeyDown(() => ensureRoute(editPath))}
                                    >
                                        <Icon
                                            name="edit"
                                            color={theme.colors.actionMinor[500]}
                                            size="large"
                                        />
                                    </span>
                                </Tooltip>
                            ) : null}
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                            <Menus
                                key={menuInstanceKey}
                                className="fm-marketing-campaign-row__menus-root"
                                classes={{ trigger: 'fm-marketing-campaign-row__action-btn' }}
                                icon={
                                    <Icon name="threeDots" color={theme.colors.actionMinor[500]} />
                                }
                                items={menuItems}
                                onVisibleChange={handleMenuVisibleChange}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default memo(MarketingCampaignRow);
